import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import type { OrderDetailResponse } from '@fitting-room/contracts'
import { MAX_INVOICE_RECIPIENTS } from '@fitting-room/contracts/order-math'
import { ChevronLeft, Plus, X } from 'lucide-react'
import { useEmailInvoice, useInvoiceEmailStatus } from '../../../lib/adminQueries'
import {
  addRecipient,
  checkRecipients,
  invoiceRecipients,
  openSuggestions,
  recipientsFull,
  recipientsReady,
  sendReadiness,
} from '../../../lib/invoice'
import { useStaffToken } from '../../../lib/staffToken'
import { FormField } from '../FormSheet'
import { DownloadPdfButton } from './InvoiceActions'

/** A To field. Its `id` outlives a neighbour's removal, so React keeps the right input — and the cursor — with it. */
interface Row {
  readonly id: number
  readonly value: string
}

/**
 * Who the invoice goes to, and the Send that sends it — beside the preview
 * on the invoice page. `docs/22` § "Invoice".
 *
 * The To fields start with the order's email (or, without one, the first
 * linked appointment's); every other address on file is offered one click
 * away, never added for the stylist (`lib/invoice.ts`).
 *
 * Send is live only once `GET /api/admin/invoice-email` has said email is set
 * up: while it is asked the panel says so, and a status that couldn't be read
 * is no answer to send on.
 *
 * `pdf` is the invoice the preview holds, for Download PDF to save as is.
 */
export function InvoiceSendPanel({ detail, pdf }: { readonly detail: OrderDetailResponse; readonly pdf: Blob | null }) {
  const { token } = useStaffToken()
  const { order } = detail
  const status = useInvoiceEmailStatus(token, true)
  const send = useEmailInvoice(token, order.id)
  const start = useMemo(() => invoiceRecipients(detail), [detail])
  const nextId = useRef(start.prefill.length)
  const [rows, setRows] = useState<Row[]>(() => start.prefill.map((value, id) => ({ id, value })))
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [answeredNotConfigured, setAnsweredNotConfigured] = useState(false)
  const [sentTo, setSentTo] = useState<readonly string[] | null>(null)

  // Where the cursor goes after a chip fills a field or a field is removed —
  // applied once React has drawn the field it names.
  const inputs = useRef(new Map<number, HTMLInputElement>())
  const [focusRow, setFocusRow] = useState<number | null>(null)
  useEffect(() => {
    if (focusRow === null) return
    inputs.current.get(focusRow)?.focus()
    setFocusRow(null)
  }, [focusRow])

  const recipients = rows.map((row) => row.value)
  const readiness = sendReadiness({ isError: status.isError, configured: status.data?.configured, answeredNotConfigured })
  const check = checkRecipients(recipients)
  const suggestions = openSuggestions(start, recipients)
  const full = recipientsFull(recipients)

  const newRow = (value: string): Row => ({ id: nextId.current++, value })

  const suggest = (address: string) => {
    const { index } = addRecipient(recipients, address)
    if (index === null) return
    const existing = rows[index]
    if (existing) {
      setRows(rows.map((row) => (row.id === existing.id ? { ...row, value: address } : row)))
      setFocusRow(existing.id)
    } else {
      const row = newRow(address)
      setRows([...rows, row])
      setFocusRow(row.id)
    }
  }

  const remove = (id: number) => {
    const at = rows.findIndex((row) => row.id === id)
    const rest = rows.filter((row) => row.id !== id)
    setRows(rest)
    const neighbour = rest[at] ?? rest[at - 1]
    if (neighbour) setFocusRow(neighbour.id)
  }

  const addBlank = () => {
    const row = newRow('')
    setRows([...rows, row])
    setFocusRow(row.id)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setSubmitted(true)
    if (readiness !== 'ready' || !recipientsReady(check)) return
    setSentTo(null)
    send.mutate(
      { to: check.to, ...(message.trim() ? { message: message.trim() } : {}) },
      {
        onSuccess: (result) => {
          if (result.sent) {
            setSentTo(result.recipients)
            setSubmitted(false)
          } else setAnsweredNotConfigured(true)
        },
      },
    )
  }

  return (
    <form onSubmit={submit} noValidate className="st-card px-4 py-4" aria-label="Send the invoice">
      <h2 className="st-eyebrow mb-3">Send it</h2>

      {readiness === 'checking' ? (
        <p className="st-meta mb-3 text-sm" role="status" data-testid="email-checking">
          Checking email…
        </p>
      ) : null}
      {readiness === 'not_configured' ? (
        <p className="st-notice mb-3 text-sm" role="status" data-testid="email-not-configured">
          Email isn&rsquo;t set up for the console yet, so nothing can be sent from here. Download the PDF and send it yourself
          for now.
        </p>
      ) : null}
      {readiness === 'unknown' ? (
        <div className="st-alert mb-3 text-sm" role="alert" data-testid="email-status-failed">
          <p>
            Couldn&rsquo;t check whether email is set up, so nothing can be sent from here right now. Try again, or download the
            PDF and send it yourself.
          </p>
          <button
            type="button"
            className="st-btn-ghost mt-2"
            disabled={status.isFetching}
            onClick={() => void status.refetch()}
          >
            {status.isFetching ? 'Checking email…' : 'Try again'}
          </button>
        </div>
      ) : null}
      {sentTo ? (
        <p className="st-ok mb-3 break-words text-sm" role="status" data-testid="invoice-sent">
          Invoice emailed to {sentTo.join(', ')}. It&rsquo;s in the order&rsquo;s history.
        </p>
      ) : null}

      <fieldset className="mb-3 min-w-0">
        <legend className="st-eyebrow mb-1.5">To</legend>
        <ul className="flex flex-col gap-2">
          {rows.map((row, index) => (
            <li key={row.id}>
              <div className="flex items-center gap-1.5">
                <input
                  ref={(el) => {
                    if (el) inputs.current.set(row.id, el)
                    else inputs.current.delete(row.id)
                  }}
                  className="st-input min-w-0 flex-1"
                  type="email"
                  autoComplete="off"
                  aria-label={index === 0 ? 'Email address' : `Email address ${index + 1}`}
                  placeholder="name@example.com"
                  value={row.value}
                  onChange={(event) => {
                    const value = event.target.value
                    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, value } : r)))
                  }}
                />
                {rows.length > 1 ? (
                  <button
                    type="button"
                    className="st-iconbtn shrink-0"
                    aria-label={`Remove ${row.value.trim() || 'this address'}`}
                    onClick={() => remove(row.id)}
                  >
                    <X size={14} aria-hidden />
                  </button>
                ) : null}
              </div>
              {submitted && check.fields[index] ? (
                <span className="text-xs" style={{ color: '#8d3f2c' }} role="alert">
                  {check.fields[index]}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        {suggestions.length > 0 && !full ? (
          <div className="mt-2" data-testid="recipient-suggestions">
            <p className="st-meta mb-1 text-xs">On file:</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((address) => (
                <button
                  key={address}
                  type="button"
                  className="st-chip min-w-0 max-w-full"
                  aria-label={`Add ${address}`}
                  onClick={() => suggest(address)}
                >
                  <Plus size={12} aria-hidden className="shrink-0" />
                  <span className="truncate">{address}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {rows.length < MAX_INVOICE_RECIPIENTS ? (
          <button type="button" className="st-btn-quiet -ml-2 mt-1.5" onClick={addBlank}>
            <Plus size={13} aria-hidden />
            Add another address
          </button>
        ) : (
          <p className="st-meta mt-1.5 text-xs">An invoice goes to at most {MAX_INVOICE_RECIPIENTS} addresses.</p>
        )}
        {submitted && check.problem ? (
          <p className="mt-1 text-xs" style={{ color: '#8d3f2c' }} role="alert">
            {check.problem}
          </p>
        ) : null}
      </fieldset>

      <FormField label="Message (optional)" hint="Goes above the invoice in the email.">
        <textarea
          className="st-input"
          rows={4}
          maxLength={2000}
          value={message}
          placeholder="Like: Thank you for choosing us! Here's your invoice."
          onChange={(event) => setMessage(event.target.value)}
        />
      </FormField>

      {send.isError ? (
        <p className="st-alert mb-3 text-sm" role="alert" data-testid="invoice-send-error">
          {send.error.message}
        </p>
      ) : null}

      <button type="submit" className="st-btn w-full" disabled={readiness !== 'ready' || send.isPending}>
        {send.isPending ? 'Sending…' : 'Send invoice'}
      </button>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3" style={{ borderColor: 'var(--st-rule)' }}>
        <DownloadPdfButton detail={detail} blob={pdf} />
        <Link to={`/admin/orders/${encodeURIComponent(order.id)}`} className="st-btn-quiet -mr-2">
          <ChevronLeft size={14} aria-hidden />
          Back to the order
        </Link>
      </div>
    </form>
  )
}
